import { deepCopy } from "./deepCopy";

const TIME_TO_MILLISECONDS = 1000;

export const EPG_MODAL_TYPES = {
  VERTICAL_SIDEBAR_EPG_POPUP: "VERTICAL_SIDEBAR_EPG_POPUP",
  VERTICAL_SIDEBAR_SUBSCRIPTION_POPUP: "VERTICAL_SIDEBAR_SUBSCRIPTION_POPUP"
};

export const EPG_VIEWS = {
  dynamic: "dynamic",
  static: "static"
};

export const CHANNEL_TYPES = {
  linear: "linear",
  playlist: "playlist"
};

export const WIDEVINE_FORMATS = {
  withAd: "dash_widevine_ads_spa",
  withoutAd: "dash_widevine_spa",
  withAdnonDrm : "dash_widevine_fp_ads_spa",
  withoutAdnonDrm : "dash_widevine_fp_spa",
  withAdnonDrmProtected : "dash_widevine_tk_ads_spa",
  withoutAdnonDrmProtected : "dash_widevine_tk_spa"
};

export const DPLAY = {
  free:"free",
  token: "token"
}

export const ARROW_DIRECTIONS = {
  up: "up",
  down: "down",
  left: "left",
  right: "right"
};

export const EVENT_STATUS = {
  ended: "ended",
  live: "live",
  upcoming: "upcoming",
  watchNow: "watchNow"
};

export const TAG_STATUS = {
  ended: "ended",
  live: "live",
  upcoming: "upcoming",
  watchNow: "watchNow"
};

export const IMAGE_TYPES = {
  logoPng: "logo-png",
  logoSvg: "logo-svg",
  landscapePosterV1: "landscape_poster_v1",
  darkLogoPng: "dark-logo-png"
};

export const CHANNEL_PLAN = {
  svod: "SVOD",
  avod: "AVOD"
};

export function convertToTitleCase(str) {
  return str.toLowerCase().replace(/\b\w/g, s => s.toUpperCase());
}

export const getStatusTagText = (status, channelType) => {
  if (channelType === CHANNEL_TYPES.playlist && status === EVENT_STATUS.live) {
    status = EVENT_STATUS.watchNow;
  }
  switch (status) {
    case EVENT_STATUS.ended:
      return TAG_STATUS.ended;

    case EVENT_STATUS.live:
      return TAG_STATUS.live;

    case EVENT_STATUS.upcoming:
      return TAG_STATUS.upcoming;

    case EVENT_STATUS.watchNow:
      return TAG_STATUS.watchNow;

    default:
      return TAG_STATUS.live;
  }
};

export const getCurrentDate = () => {
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const d = new Date();
  return d.getDate() + " " + months[d.getMonth()] + ", " + d.getFullYear();
};

export const getSlotTimeInHours = timeStamp => {
  const d = new Date(timeStamp * TIME_TO_MILLISECONDS);
  let hours = d.getHours();
  if (hours) return hours < 10 ? `0${hours}:00` : `${hours}:00`;
};

export const calculateDurationForSlots = (startTime, endTime) => {
  const started = new Date(startTime * TIME_TO_MILLISECONDS);
  const ended = new Date(endTime * TIME_TO_MILLISECONDS);
  const startHour = started.getHours();
  const endHour = ended.getHours();
  const startMins = started.getMinutes();
  const endMins = ended.getMinutes();
  const to = (startHour < 10 ? `0${startHour}` : startHour) + ":" + (startMins < 10 ? `0${startMins}` : startMins);
  const from = (endHour < 10 ? `0${endHour}` : endHour) + ":" + (endMins < 10 ? `0${endMins}` : endMins);
  return `${to} - ${from}`;
};

export const getCurrentChannelImage = (channel, imageType) => {
  const logoPng = channel.images.filter(image => image.type === imageType);
  return logoPng.length > 0 ? logoPng[0].url : "";
};

export const getCurrentEventPosterImage = (event, imageType) => {
  const posterImage = event.images.filter(image => image.type === imageType);
  return posterImage.length > 0 ? posterImage[0].url : "";
};

export const doNothing = () => {
  return;
}

export const getVideoMetaData = (epgData, categoryIndex = 0, rowIndex = 0, colIndex = 0) => {
  const channel = epgData[categoryIndex].data[rowIndex];
  const event = channel?.events[colIndex];
  const channelImage = getCurrentChannelImage(channel, IMAGE_TYPES.logoPng);
  const eventPlaceholderPosterImage = getCurrentEventPosterImage(event, IMAGE_TYPES.landscapePosterV1);
  const videoMetaData = {
    ...event,
    channelImage,
    parentalControl: event.parentalControl,
    isThisDeviceSupported: channel?.isThisDeviceSupported,
    isUserSubscribedToChannel: channel?.isUserSubscribedToChannel,
    eventPosterImage: eventPlaceholderPosterImage,
    channelType: channel?.type,
    currentChannel: { ...channel }
  };
  return videoMetaData;
};

export function reduceDataForStaticView(data) {
  const deepCopiedData = deepCopy(data);
  const updatedArray = deepCopiedData.map(item => {
    item.data = item.data.map(innerItem => ({
      ...innerItem,
      events: innerItem.events.filter(event => event.status !== EVENT_STATUS.ended).slice(0, 3)
    }));

    return item;
  });
  return updatedArray;
}

export const debounce = (fn, time) => {
  let timeout;
  return function(...args) {
    const functionCall = () => fn.apply(this, args);
    clearTimeout(timeout);
    timeout = setTimeout(functionCall, time);
  };
};

export function getEpgCategoriesSlugsString(categories) {
  let slugStr = "";
  categories.forEach(element => {
    if (slugStr !== "") slugStr += ",";
    slugStr += element.slug;
  });
  return slugStr;
}

export function getTagsList(genres, parentalControl) {
  const tagsList = (genres && [...genres]) || [];
  if (parentalControl) {
    const parentControlValue = parentalControl === 1 ? "PG-All" : `PG-${parentalControl}`;
    tagsList.push(parentControlValue);
  }
  return tagsList;
}

export function getProgress(tsStart, tsEnd) {
  const today = new Date();
  const startTime = new Date(tsStart * TIME_TO_MILLISECONDS);
  const endTime = new Date(tsEnd * TIME_TO_MILLISECONDS);
  const differenceProgramMilliseconds = endTime.getTime() - startTime.getTime();

  const remMilliseconds = endTime.getTime() - today.getTime();
  let time = new Date(remMilliseconds);
  let hoursRem = time.getUTCHours();
  let minutesRem = time.getUTCMinutes();
  let secondsRem = time.getUTCSeconds();
  if (secondsRem > 0) {
    minutesRem += 1;
  }

  const totalProgramMinutes = Math.round(differenceProgramMilliseconds / 60000);
  const minutes = hoursRem * 60 + minutesRem;
  const progress = ((totalProgramMinutes - minutes) * 100) / totalProgramMinutes;
  return progress;
}

export function getRemainingTime(event, isOnUI = true) {
  let startTime = new Date();
  let endTime = new Date(event.tsEnd * TIME_TO_MILLISECONDS);
  let difference = endTime.getTime() - startTime.getTime(); // This will give difference in milliseconds
  let resultInMinutes = Math.round(difference / 60000);
  if (isOnUI) {
    resultInMinutes = resultInMinutes < 1 ? 0 : resultInMinutes;
  }
  return resultInMinutes;
}

export function setCacheKeyForCategories(categories) {
  const fromCacheObj = { all: false };
  categories.forEach(element => {
    fromCacheObj[element.slug] = false;
  });
  return fromCacheObj;
}

export function isToGetLatestCategoryData(epgData, categorySlug, isAllCategory) {
  let isAvoidCache = false;
  for (let i = 0; i < epgData.length; i++) {
    const element = epgData[i];
    if (element.heading.slug === categorySlug) {
      const selectedCategoryData = element.data;
      for (let j = 0; j < selectedCategoryData.length; j++) {
        const channel = selectedCategoryData[j];
        for (let k = 0; k < channel.events.length; k++) {
          const event = channel.events[k];
          if (event.status === EVENT_STATUS.live || event.status === EVENT_STATUS.watchNow) {
            const remainingTime = getRemainingTime(event, false);
            if (remainingTime <= 0) {
              isAvoidCache = true;
              break;
            }
          }
        }
        if (isAvoidCache) {
          break;
        }
      }
      if (isAvoidCache) {
        break;
      }
    }
  }
  return isAvoidCache;
}
